import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/boards";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate(next);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-950 text-white">
        <Link to="/" className="font-display text-xl font-black tracking-tighter" data-testid="login-logo">
          KANBN<span className="text-red-500">.</span>
        </Link>
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">
            sign in
          </div>
          <p className="font-display text-4xl font-black tracking-tighter leading-tight max-w-md">
            Get back to the boards that actually move work forward.
          </p>
        </div>
        <div className="font-mono-tight text-[10px] uppercase tracking-widest text-zinc-500">
          v1.0 / 2026
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white dark:bg-zinc-950">
        <div className="max-w-md w-full mx-auto">
          <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
            01 / authenticate
          </div>
          <h2 className="font-display text-4xl font-black tracking-tighter mb-2 text-zinc-950 dark:text-zinc-100">Sign in.</h2>
          <p className="text-sm text-zinc-500 mb-10">
            New here?{" "}
            <Link to="/register" className="text-black dark:text-white underline underline-offset-4" data-testid="login-register-link">
              Create an account
            </Link>
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-zinc-900 dark:text-zinc-100">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 underline underline-offset-2 transition-colors"
                >
                  Forgot?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="border border-red-500 text-red-600 px-4 py-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black dark:bg-white text-white dark:text-black py-3.5 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
